import React from 'react'
import { Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'

import {
  Card,
  CardContent,
  Typography,
  Chip,
  IconButton,
  CircularProgress,
  Box,
  Divider,
  Skeleton,
  Tooltip
} from '@mui/material'
import {
  DeleteForever,
  Refresh,
  ArrowForward
} from '@mui/icons-material'

import Flag from '../../mailTemplate/src/components/atomic/atoms/Flag'
import EmailStatusPercentageBar from '../../mailTemplate/src/components/atomic/atoms/EmailStatusPercentageBar'
import EmailStatusItemList from '../../mailTemplate/src/components/atomic/atoms/EmailStatusItemList'
import HsChipList from '../../mailTemplate/src/components/atomic/molecules/HsChipList'

import { useGlobalStoreData } from '../../store/globalStore'

const getStatusConfig = (status, t) => {
  const configs = {
    SUBMITTED: { label: t('mailTemplate.campaigns.status.SUBMITTED'), color: 'primary', bgColor: '#e3f2fd' },
    PREPARING: { label: t('mailTemplate.campaigns.status.PREPARING'), color: 'warning', bgColor: '#fff3e0' },
    READY_TO_SELECT: { label: t('mailTemplate.campaigns.status.READY_TO_SELECT'), color: 'info', bgColor: '#e1f5fe' },
    GENERATING: { label: t('mailTemplate.campaigns.status.GENERATING'), color: 'info', bgColor: '#e3f2fd' },
    READY_TO_MAIL: { label: t('mailTemplate.campaigns.status.READY_TO_MAIL'), color: 'success', bgColor: '#e8f5e8' },
    MAILING: { label: t('mailTemplate.campaigns.status.MAILING'), color: 'secondary', bgColor: '#f3e5f5' },
    FAILED: { label: t('mailTemplate.campaigns.status.FAILED'), color: 'error', bgColor: '#ffebee' }
  }
  return configs[status] || null
}

const CampaignCardHeader = ({ campaignItem, statusConfig }) => {
  const productNames = campaignItem.productName || []
  const maxVisible = 2

  return (
    <Box sx={{ mb: 0.25 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 0 }}>
        <Box sx={{ flex: 1, minWidth: 0, mr: 2 }}>
          <Typography
            variant="h6"
            sx={{
              fontWeight: 600,
              mb: 0.5,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              color: 'text.primary',
              lineHeight: 1.2
            }}
          >
            {campaignItem.name}
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, alignItems: 'center' }}>
            {productNames.slice(0, maxVisible).map((product, index) => (
              <Chip
                key={index}
                label={product}
                size="small"
                sx={{
                  borderRadius: 1.5,
                  height: 20,
                  fontSize: '0.75rem',
                  backgroundColor: 'action.hover',
                  '& .MuiChip-label': {
                    px: 1
                  }
                }}
              />
            ))}
            {productNames.length > maxVisible && (
              <Tooltip
                title={productNames.slice(maxVisible).join(', ')}
                arrow
              >
                <Chip
                  label={`+${productNames.length - maxVisible}`}
                  size="small"
                  sx={{
                    borderRadius: 1.5,
                    height: 20,
                    fontSize: '0.75rem',
                    backgroundColor: 'action.selected',
                    fontWeight: 500,
                    '& .MuiChip-label': {
                      px: 1
                    }
                  }}
                />
              </Tooltip>
            )}
          </Box>
        </Box>
        {statusConfig && (
          <Chip
            label={statusConfig.label}
            color={statusConfig.color}
            size="small"
            sx={{
              fontWeight: 500,
              borderRadius: 1.5,
              height: 20
            }}
          />
        )}
      </Box>

      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', my: 0.25, mt: 2 }}>
      <Flag
        code={campaignItem.currentCountryCode}
        square
        style={{
          fontSize: '1.75rem',
          borderRadius: '6px',
          boxShadow: '0 1px 4px rgba(0,0,0,0.1)'
        }}
      />
      <ArrowForward sx={{ mx: 1, color: 'primary.main', fontSize: '1.25rem' }} />
      <Flag
        code={campaignItem.importerCountryCodes?.[0]}
        square
        style={{
          fontSize: '1.75rem',
          borderRadius: '6px',
          boxShadow: '0 1px 4px rgba(0,0,0,0.1)'
        }}
      />
    </Box>
  </Box>
  )
}

const CampaignCardStats = ({ campaignReport, languageGSD, isLoading }) => {
  if (isLoading || !campaignReport) {
    return (
      <Box sx={{ mb: 0.25, mt: 4 }}>
        <Box sx={{ p: 0.25, width: '100%' }}>

          <Box sx={{
            display: 'flex',
            height: '20px',
            width: '100%',
            borderRadius: 1,
            overflow: 'hidden',
            mb: 0.125
          }}>
            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, width: '100%' }}>
              <Skeleton variant="rectangular" width="35px" height="10px" sx={{ borderRadius: 0.5 }} />
              <Skeleton variant="rectangular" width="30px" height="10px" sx={{ borderRadius: 0.5 }} />
              <Skeleton variant="rectangular" width="40px" height="10px" sx={{ borderRadius: 0.5 }} />
            </Box>
          </Box>

          <Skeleton variant="rectangular" width="100%" height={16} sx={{ borderRadius: 1 }} />
        </Box>

        <Box sx={{ width: '100%', display: 'flex', gap: 1.5, pb: 1, pt: 2.5 }}>
          <Box
           sx={{
             flex: 1,
             display: 'flex',
             borderLeft: 'none',
             borderColor: 'divider'
           }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Box sx={{
                  width: 32,
                  height: 32,
                  borderRadius: 1.5,
                  backgroundColor: 'action.hover',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}>
                  <Skeleton variant="circular" width={18} height={18} />
                </Box>
                <Box>
                  <Skeleton variant="text" width="28px" height={14} sx={{ mb: 0.25 }} />
                  <Skeleton variant="text" width="42px" height={12} />
                </Box>
              </Box>
            </Box>
            <Box
           sx={{
             flex: 1,
             display: 'flex',
             borderLeft: 'none',
             borderColor: 'divider'
           }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Box sx={{
                  width: 32,
                  height: 32,
                  borderRadius: 1.5,
                  backgroundColor: 'action.hover',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}>
                  <Skeleton variant="circular" width={18} height={18} />
                </Box>
                <Box>
                  <Skeleton variant="text" width="24px" height={14} sx={{ mb: 0.25 }} />
                  <Skeleton variant="text" width="48px" height={12} />
                </Box>
              </Box>
            </Box>
            <Box
           sx={{
             flex: 1,
             display: 'flex',
             borderLeft: 'none',
             borderColor: 'divider'
           }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Box sx={{
                  width: 32,
                  height: 32,
                  borderRadius: 1.5,
                  backgroundColor: 'action.hover',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}>
                  <Skeleton variant="circular" width={18} height={18} />
                </Box>
                <Box>
                  <Skeleton variant="text" width="32px" height={14} sx={{ mb: 0.25 }} />
                  <Skeleton variant="text" width="38px" height={12} />
                </Box>
              </Box>
            </Box>
        </Box>
      </Box>
    )
  }

  return (
    <Box sx={{ mb: 0.25 }}>
      <EmailStatusPercentageBar emailStats={campaignReport?.outgoingEmailStats} languageGSD={languageGSD} />
      <EmailStatusItemList campaignReport={campaignReport} languageGSD={languageGSD} />
    </Box>
  )
}

const CampaignCardFooter = ({ campaignItem, setConfirmationModalItem, refreshCampaignReport, campaignReportFetching }) => (
  <Box>
    <Divider sx={{ mb: 0.25 }} />
    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', minHeight: 32 }}>
      <Box sx={{ display: 'flex', gap: 0.25 }}>
        <IconButton
          onClick={(e) => {
            e.preventDefault()
            setConfirmationModalItem(campaignItem)
          }}
          size="small"
          sx={{
            color: 'error.main',
            padding: 0.5,
            '&:hover': {
              backgroundColor: 'error.light',
              color: 'error.dark'
            }
          }}
        >
          <DeleteForever fontSize="small" />
        </IconButton>
        <IconButton
          onClick={(e) => {
            e.preventDefault()
            !campaignReportFetching && refreshCampaignReport(campaignItem.id)
          }}
          size="small"
          sx={{
            color: 'primary.main',
            padding: 0.5,
            '&:hover': {
              backgroundColor: 'primary.light',
              color: 'primary.dark'
            }
          }}
        >
          {campaignReportFetching
            ? <CircularProgress size={14} />
            : <Refresh fontSize="small" />
          }
        </IconButton>
      </Box>

      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.25, justifyContent: 'flex-end' }}>
        <HsChipList hsArr={campaignItem?.hsCodes} />
      </Box>
    </Box>
  </Box>
)

const CampaignCard = ({
  campaignItem,
  setConfirmationModalItem,
  campaignReport,
  refreshCampaignReport,
  campaignReportFetching
}) => {
  const languageGSD = useGlobalStoreData.language()
  const { t } = useTranslation()

  const statusConfig = getStatusConfig(campaignItem.status, t)

  const getLinkTarget = (campaignData) => {
    const baseUrl = `${process.env.PUBLIC_URL}/${languageGSD}`
    const params = `?b2bAccountId=${campaignData.b2bAccountId}&b2bCampaignId=${campaignData.id}`

    return `${baseUrl}/campaigns/detail${params}`
  }

  return (
    <Link to={getLinkTarget(campaignItem)} style={{ textDecoration: 'none' }}>
      <Card
        sx={{
          height: '100%',
          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
          cursor: 'pointer',
          borderRadius: 3,
          border: '1px solid',
          borderColor: 'divider',
          '&:hover': {
            transform: 'translateY(-4px)',
            boxShadow: '0 12px 24px rgba(0,0,0,0.15)',
            borderColor: 'primary.main',
            '& .card-title': {
              color: 'primary.main'
            }
          },
          display: 'flex',
          flexDirection: 'column'
        }}
      >
        <CardContent sx={{
          flex: 1,
          p: 3,
          display: 'flex',
          flexDirection: 'column',
          '&:last-child': {
            paddingBottom: 1
          }
        }}>
          <CampaignCardHeader campaignItem={campaignItem} statusConfig={statusConfig} />

          <Box sx={{ flex: 1 }}>
            <CampaignCardStats
              campaignReport={campaignReport}
              languageGSD={languageGSD}
              isLoading={campaignReportFetching}
            />
          </Box>

          <CampaignCardFooter
            campaignItem={campaignItem}
            setConfirmationModalItem={setConfirmationModalItem}
            refreshCampaignReport={refreshCampaignReport}
            campaignReportFetching={campaignReportFetching}
          />
        </CardContent>
      </Card>
    </Link>
  )
}

export default CampaignCard
